import { Container } from "components";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "services/api";
import JoditEditor from "jodit-react";
import { useToaster } from "hooks";
import * as Yup from "yup";

const initialValues = {
  name: "",
  parent_id: "",
  industry_id: "",
  contact_email: "",
  contact_phone: "",
  address: "",
  plan_name: "",
  plan_short_desc: "",
  plan_long_desc: "",
  plan_price: "",
  plan_duration: "",
  plan_duration_type: "",
  plan_status: "",
  membership_type: "",
  no_of_users: "",
  no_of_users_user_can_add: "",
  no_of_users_remaining: "",
};

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Required")
    .matches(/^[A-Za-z ]+$/, "Only Alphabets"),
  industry_id: Yup.string(),
  contact_email: Yup.string()
    .email("Invalid email address")
    .required("Required")
    .matches(
      /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
      "Invalid email format"
    ),
  contact_phone: Yup.string()
    .required("Required")
    .matches(/[0-9]+$/, "Only numeric")
    .length(10, "Phone number must be exactly 10 digits"),
  address: Yup.string()
    .required("Required")
    .matches(/^[A-Za-z0-9' -;:,.&]+$/),
  plan_name: Yup.string().required("Required"),
  plan_short_desc: Yup.string().required("Required"),
  plan_long_desc: Yup.string().required("Required"),
  plan_price: Yup.number().min(0, "Price must be >= 0").required("Required"),
  plan_duration: Yup.number()
    .min(1, "Duration must be >= 1")
    .required("Required"),
  plan_duration_type: Yup.string()
    .oneOf(["Days", "Months", "Years"], "Invalid duration type")
    .required("Required"),
  plan_status: Yup.string()
    .oneOf(["Active", "Inactive"], "Invalid status")
    .required("Required"),
  membership_type: Yup.string()
    .oneOf(["Individual", "Family"], "Invalid membership type")
    .required("Required"),
  no_of_users: Yup.number()
    .min(0, "Number of users must be >= 0")
    .required("Required"),
  no_of_users_user_can_add: Yup.number()
    .min(0, "Number of users must be >= 0")
    .nullable(),
  no_of_users_remaining: Yup.number()
    .min(0, "Number of users must be >= 0")
    .nullable(),
});

export const AddCorperators = () => {
  const [industries, setIndustries] = useState([]);
  const [formData, setFormData] = useState(initialValues);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const params = useParams();
  const { setSuccessToaster, setErrorToaster } = useToaster();

  const fetchIndustries = async () => {
    try {
      const response = await api.get("/industries");
      const result = response?.data?.data || [];
      setIndustries(result);
      console.log("fetchIndustries:", result);
    } catch (error) {
      console.log("fetchIndustries Error:", error);
    }
  };

  const fetchCorporateDetails = async () => {
    if (params.id) {
      try {
        setIsLoading(true);
        const response = await api.get(`/corporate-entities/${params.id}`);
        const corporateData = response?.data?.data;
        if (corporateData) {
          setFormData({
            name: corporateData.name || "",
            parent_id: corporateData.parent_id || "",
            industry_id: corporateData.industry_id || "",
            contact_email: corporateData.contact_email || "",
            contact_phone: corporateData.contact_phone || "",
            address: corporateData.address || "",
            plan_name: corporateData.plan_name || "",
            plan_short_desc: corporateData.plan_short_desc || "",
            plan_long_desc: corporateData.plan_long_desc || "",
            plan_price: corporateData.plan_price || "",
            plan_duration: corporateData.plan_duration || "",
            plan_duration_type: corporateData.plan_duration_type || "",
            plan_status: corporateData.plan_status || "",
            membership_type: corporateData.membership_type || "",
            no_of_users: corporateData.no_of_users || "",
            no_of_users_user_can_add:
              corporateData.no_of_users_user_can_add || "",
            no_of_users_remaining: corporateData.no_of_users_remaining || "",
          });
        }
      } catch (error) {
        console.log("fetchCorporateDetails Error:", error);
        setErrorToaster("Failed to fetch corporate details");
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchIndustries();
    if (params.id) {
      fetchCorporateDetails();
    }
  }, [params.id]);

  const handleSubmit = async (values) => {
    try {
      const payload = {
        ...values,
      };
      console.log("Final payload to API:", payload);

      let response;
      if (params.id) {
        // Update existing corporate
        response = await api.put(`/corporate-entities/${params.id}`, payload);
        setSuccessToaster("Corporate updated successfully!");
      } else {
        // Create new corporate
        response = await api.post("/corporate-entities", payload);
        setSuccessToaster("Corporate added successfully!");
      }

      console.log("handleSubmit:", response);
      navigate("/corporatesUsers");
    } catch (error) {
      console.log("handleSubmit Error:", error);
      setErrorToaster(
        error?.response?.data?.message || "Failed to save corporate"
      );
    }
  };

  if (isLoading) {
    return (
      <Container>
        <div className="content-wrapper">
          <div className="container-xxl flex-grow-1 container-p-y">
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "400px" }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="content-wrapper">
        <div class="container-xxl flex-grow-1 container-p-y">
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
              <li class="breadcrumb-item">
                <Link to="/corporatesUsers">Corporates</Link>
              </li>
              <li class="breadcrumb-item active">
                {params.id ? "Edit" : "Add"} Corporate
              </li>
            </ol>
          </nav>
          <div className="row">
            <div className="col-md-12">
              <Formik
                initialValues={formData}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={(values) => {
                  console.log("values:", values);
                  handleSubmit(values);
                }}
              >
                {({ setFieldValue, values }) => (
                  <>
                    <Form>
                      <div className="card mb-4">
                        <h5 class="card-header">
                          {params.id ? "Edit" : "Add"} Corporate Details
                        </h5>

                        <div className="card-body">
                          {/* Corporate Information Section */}
                          <h6 className="mb-3">Corporate Information</h6>
                          <div className="row mb-3">
                            <div className="col-md-4 col-12">
                              <label for="name" className="form-label">
                                Corporate Name{" "}
                                <span className="invalid-form">*</span>
                              </label>
                              <Field
                                type="text"
                                className={`form-control`}
                                name="name"
                                placeholder="Corporate Name"
                                autofocus
                                onChange={(e) => {
                                  if (/^[A-Za-z ]*$/.test(e.target.value)) {
                                    setFieldValue("name", e.target.value);
                                  }
                                }}
                              />
                              <ErrorMessage
                                name="name"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                            <div className="col-md-4 col-12">
                              <label for="contact_email" className="form-label">
                                Contact Email{" "}
                                <span className="invalid-form">*</span>
                              </label>
                              <Field
                                type="email"
                                className={`form-control`}
                                name="contact_email"
                                placeholder="Enter your Email"
                                autofocus
                                onChange={(e) => {
                                  const email = e.target.value;
                                  const isValidEmail = /^[A-Za-z0-9@.]*$/.test(
                                    email
                                  );
                                  if (isValidEmail) {
                                    setFieldValue("contact_email", email);
                                  }
                                }}
                              />
                              <ErrorMessage
                                name="contact_email"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                            <div className="col-md-4 col-12">
                              <label for="contact_phone" className="form-label">
                                Contact Phone No{" "}
                                <span className="invalid-form">*</span>
                              </label>
                              <Field
                                type="tel"
                                className={`form-control`}
                                name="contact_phone"
                                placeholder="Enter your Phone No"
                                autofocus
                                value={values.contact_phone}
                                onChange={(e) => {
                                  if (/^[0-9]*$/.test(e.target.value)) {
                                    setFieldValue(
                                      "contact_phone",
                                      e.target.value
                                    );
                                  }
                                }}
                                maxLength="10"
                              />
                              <ErrorMessage
                                name="contact_phone"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                          </div>
                          <div className="row mb-3">
                            <div className="col-md-6 col-12">
                              <label for="industry_id" className="form-label">
                                Industry
                              </label>
                              <Field
                                as="select"
                                className={`select2 form-select`}
                                name="industry_id"
                              >
                                <option value="">Select Industry</option>
                                {industries.map((industry) => (
                                  <option key={industry.id} value={industry.id}>
                                    {industry.name}
                                  </option>
                                ))}
                              </Field>
                              <ErrorMessage
                                name="industry_id"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                            <div className="col-md-6 col-12">
                              <label for="parent_id" className="form-label">
                                Select Parent Company
                              </label>
                              <Field
                                as="select"
                                className={`select2 form-select`}
                                name="parent_id"
                              >
                                <option value="">Select Parent Company</option>
                                {/* Add parent companies here when API is available */}
                              </Field>
                              <ErrorMessage
                                name="parent_id"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                          </div>
                          <div className="row mb-3">
                            <div className=" col-12">
                              <label for="address" className="form-label">
                                Corporate Address
                                <span className="invalid-form">*</span>
                              </label>
                              <Field
                                type="text"
                                className={`form-control`}
                                name="address"
                                placeholder="Corporate Address"
                                autofocus
                                onChange={(e) => {
                                  if (
                                    /^[A-Za-z0-9 .,:;'&-]*$/.test(
                                      e.target.value
                                    )
                                  ) {
                                    setFieldValue("address", e.target.value);
                                  }
                                }}
                              />
                              <ErrorMessage
                                name="address"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                          </div>

                          {/* Plan Information Section */}
                          <hr className="my-4" />
                          <h6 className="mb-3">Plan Information</h6>
                          <div className="row mb-3">
                            <div className="col-md-6 col-12">
                              <label for="plan_name" className="form-label">
                                Plan Name{" "}
                                <span className="invalid-form">*</span>
                              </label>
                              <Field
                                type="text"
                                className={`form-control`}
                                name="plan_name"
                                placeholder="Plan Name"
                              />
                              <ErrorMessage
                                name="plan_name"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                            <div className="col-md-6 col-12">
                              <label for="plan_price" className="form-label">
                                Plan Price{" "}
                                <span className="invalid-form">*</span>
                              </label>
                              <Field
                                type="number"
                                min="0"
                                className={`form-control`}
                                name="plan_price"
                                placeholder="Plan Price"
                              />
                              <ErrorMessage
                                name="plan_price"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                          </div>

                          <div className="row mb-3">
                            <div className="col-md-4 col-12">
                              <label for="plan_status" className="form-label">
                                Plan Status{" "}
                                <span className="invalid-form">*</span>
                              </label>
                              <Field
                                as="select"
                                name="plan_status"
                                className="select2 form-select"
                              >
                                <option value="">Select Status</option>
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                              </Field>
                              <ErrorMessage
                                name="plan_status"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                            <div className="col-md-4 col-12">
                              <label
                                for="membership_type"
                                className="form-label"
                              >
                                Membership Type{" "}
                                <span className="invalid-form">*</span>
                              </label>
                              <Field
                                as="select"
                                name="membership_type"
                                className="select2 form-select"
                              >
                                <option value="">Select Type</option>
                                <option value="Individual">Individual</option>
                                <option value="Family">Family</option>
                              </Field>
                              <ErrorMessage
                                name="membership_type"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                            <div className="col-md-4 col-12">
                              <label for="no_of_users" className="form-label">
                                Number of Users{" "}
                                <span className="invalid-form">*</span>
                              </label>
                              <Field
                                type="number"
                                min="0"
                                className={`form-control`}
                                name="no_of_users"
                                placeholder="Number of Users"
                              />
                              <ErrorMessage
                                name="no_of_users"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                          </div>

                          <div className="row mb-3">
                            <div className="col-md-4 col-12">
                              <label for="plan_duration" className="form-label">
                                Plan Duration{" "}
                                <span className="invalid-form">*</span>
                              </label>
                              <Field
                                type="number"
                                min="1"
                                className={`form-control`}
                                name="plan_duration"
                                placeholder="Duration"
                              />
                              <ErrorMessage
                                name="plan_duration"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                            <div className="col-md-4 col-12">
                              <label
                                for="plan_duration_type"
                                className="form-label"
                              >
                                Duration Type{" "}
                                <span className="invalid-form">*</span>
                              </label>
                              <Field
                                as="select"
                                name="plan_duration_type"
                                className="select2 form-select"
                              >
                                <option value="">Select Type</option>
                                <option value="Days">Days</option>
                                <option value="Months">Months</option>
                                <option value="Years">Years</option>
                              </Field>
                              <ErrorMessage
                                name="plan_duration_type"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                            <div className="col-md-4 col-12">
                              <label
                                for="no_of_users_user_can_add"
                                className="form-label"
                              >
                                Users Can Add
                              </label>
                              <Field
                                type="number"
                                min="0"
                                className={`form-control`}
                                name="no_of_users_user_can_add"
                                placeholder="Users Can Add"
                              />
                              <ErrorMessage
                                name="no_of_users_user_can_add"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                          </div>

                          <div className="row mb-3">
                            <div className="col-md-6 col-12">
                              <label
                                for="no_of_users_remaining"
                                className="form-label"
                              >
                                Users Remaining
                              </label>
                              <Field
                                type="number"
                                min="0"
                                className={`form-control`}
                                name="no_of_users_remaining"
                                placeholder="Users Remaining"
                              />
                              <ErrorMessage
                                name="no_of_users_remaining"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                          </div>

                          <div className="row mb-3">
                            <div className="col-12">
                              <label
                                for="plan_short_desc"
                                className="form-label"
                              >
                                Plan Short Description{" "}
                                <span className="invalid-form">*</span>
                              </label>
                              <JoditEditor
                                value={values?.plan_short_desc}
                                name="plan_short_desc"
                                onChange={(newContent) => {
                                  setFieldValue("plan_short_desc", newContent);
                                }}
                                className="joditeditor short-description-editor"
                              />
                              <ErrorMessage
                                name="plan_short_desc"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                          </div>

                          <div className="row mb-3">
                            <div className="col-12">
                              <label
                                for="plan_long_desc"
                                className="form-label"
                              >
                                Plan Long Description{" "}
                                <span className="invalid-form">*</span>
                              </label>
                              <JoditEditor
                                value={values?.plan_long_desc}
                                name="plan_long_desc"
                                onChange={(newContent) => {
                                  setFieldValue("plan_long_desc", newContent);
                                }}
                                className="joditeditor"
                              />
                              <ErrorMessage
                                name="plan_long_desc"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                          </div>

                          <button
                            className="btn btn-primary d-grid w-100"
                            type="submit"
                          >
                            {params.id ? "Update" : "Submit"}
                          </button>
                        </div>
                      </div>
                    </Form>
                  </>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};
